<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import playerService from '../services/player.service'
import categoryService from '../services/category.service'
import clubService from '../services/club.service'
import clubApplicationService from '../services/clubApplication.service'
import PlayerImportModal from '../components/PlayerImportModal.vue'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Get club UUID from store
const clubUuid = computed(() => authStore.initData?.club?.uuid || '')

// Get current person role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Get possible categories
const possibleCategories = computed(() => authStore.initData?.possible_categories || [])

// Check if category is accessible
const isCategoryAccessible = (categoryUuid: string) => {
  // OWNER and SUPERVISOR can access all categories
  if (currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR') {
    return true
  }
  // COACH checks possible_categories
  return possibleCategories.value.includes(categoryUuid)
}

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canShow = computed(() => hasPermission('PLAYER_SHOW'))
const canUpdate = computed(() => hasPermission('PLAYER_UPDATE'))
const canDelete = computed(() => hasPermission('PLAYER_DELETE'))
const canSeeActions = computed(() => canShow.value || canUpdate.value || canDelete.value)
const canImport = computed(() => hasPermission('PLAYER_IMPORT'))
const showImportModal = ref(false)
const showHeaderActionsDropdown = ref(false)

const openImportModal = () => {
  showHeaderActionsDropdown.value = false
  showImportModal.value = true
}

// Club applications ("Zgłoszenia do klubu")
const canSeeApplications = computed(() => hasPermission('CLUB_APPLICATION_INDEX'))
const applicationsCount = ref(0)

const fetchApplicationsCount = async () => {
  if (!clubUuid.value || !canSeeApplications.value) return
  try {
    applicationsCount.value = await clubApplicationService.getCount(clubUuid.value)
  } catch (err) {
    console.error('Fetch applications count error:', err)
  }
}

// State
const players = ref<any[]>([])
const categories = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Pagination
const currentPage = ref(1)
const perPage = ref(50)
const total = ref(0)
const lastPage = ref(1)
const fromItem = ref(0)
const toItem = ref(0)

// Filters state
const selectedCategories = ref<string[]>([])
const searchQuery = ref('')
const showCategoryDropdown = ref(false)
const showFilterModal = ref(false)

// Advanced filters (modal)
const filterSex = ref<string>('')
const filterRegisterStatus = ref<string>('')
const filterMedicalExam = ref<string>('')
const filterInsurance = ref<string>('')
const filterPayments = ref<string>('')
const filterBirthDateFrom = ref('')
const filterBirthDateTo = ref('')
const filterRegisterDateFrom = ref('')
const filterRegisterDateTo = ref('')

// Sorting
const sortField = ref('last_name')
const sortDirection = ref<'ASC' | 'DESC'>('ASC')

// Bulk actions
const selectedPlayerUuids = ref<string[]>([])
const selectAll = ref(false)
const selectAllInSystem = ref(false)
const showBulkActionsDropdown = ref(false)

// Delete modal
const showDeleteModal = ref(false)
const playerToDelete = ref<any>(null)
const deleting = ref(false)

// Add category modal
const showAddCategoryModal = ref(false)
const selectedCategoryToAdd = ref<string>('')
const addingCategory = ref(false)

// Remove category modal
const showRemoveCategoryModal = ref(false)
const selectedCategoryToRemove = ref<string>('')
const removingCategory = ref(false)

// Send message modal
const showSendMessageModal = ref(false)
const messageText = ref('')
const sendEmailWithMessage = ref(false)
const sendingMessage = ref(false)

// Add payment modal
const showAddPaymentModal = ref(false)
const paymentName = ref('')
const paymentAmount = ref<number | null>(null)
const paymentDaysToPay = ref<number | null>(null)
const paymentBankAccountUuid = ref('')
const paymentDescription = ref('')
const addingPayment = ref(false)
const bankAccounts = ref<any[]>([])

// Export modal
const showExportModal = ref(false)
const exporting = ref(false)
const exportColumns = ref<Record<string, boolean>>({
  first_name: true,
  last_name: true,
  license_number: false,
  gender: false,
  categories: false,
  birth_date: false,
  registered_at: false,
  phone: false,
  email: false,
  medical_exam_date: false,
  guardian_1: false,
  guardian_2: false,
  street: false,
  city: false,
  postal_code: false,
  school_name: false,
})

// Dropdowns state
const openDropdownId = ref<string | null>(null)
const openAccordionId = ref<string | null>(null)

// Mock data for trainings and payments
const mockTrainings = [
  { color: 'green', date: 'Pn, 12.01.2025' },
  { color: 'red', date: 'Śr, 10.01.2025' },
  { color: 'green', date: 'Pt, 08.01.2025' },
  { color: 'red', date: 'Pn, 06.01.2025' }
]

const mockPayments = [
  { color: 'green', date: 'Pn, 15.01.2025' },
  { color: 'red', date: 'Śr, 18.12.2024' },
  { color: 'green', date: 'Pt, 22.11.2024' }
]

// Fetch categories for filters
const fetchCategories = async () => {
  if (!clubUuid.value) return

  try {
    const categoriesData = await categoryService.getCategoriesForFilters(clubUuid.value)
    categories.value = categoriesData
  } catch (err: any) {
    console.error('Fetch categories error:', err)
  }
}

// Fetch players with filters
const fetchPlayers = async () => {
  if (!clubUuid.value) {
    error.value = t('dashboard.errorNoClubUuid')
    return
  }

  loading.value = true
  error.value = null

  try {
    const params: any = {
      page: currentPage.value,
      per_page: perPage.value,
      sort: sortField.value,
      sort_type: sortDirection.value
    }

    // Add filters
    if (selectedCategories.value.length > 0) {
      params.categories = selectedCategories.value
    }
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim()
    }
    if (filterSex.value) {
      params.gender = filterSex.value
    }
    if (filterRegisterStatus.value) {
      params.register_status = filterRegisterStatus.value
    }
    if (filterBirthDateFrom.value) {
      params.birth_date_from = filterBirthDateFrom.value
    }
    if (filterBirthDateTo.value) {
      params.birth_date_to = filterBirthDateTo.value
    }
    if (filterRegisterDateFrom.value) {
      params.register_date_from = filterRegisterDateFrom.value
    }
    if (filterRegisterDateTo.value) {
      params.register_date_to = filterRegisterDateTo.value
    }

    const response = await playerService.getPlayers(clubUuid.value, params)

    players.value = response.data.players || []
    total.value = response.total || 0
    currentPage.value = response.current_page || 1
    lastPage.value = response.last_page || 1
    fromItem.value = response.from || 0
    toItem.value = response.to || 0

    // Add mock trainings and payments to each player
    players.value = players.value.map(player => ({
      ...player,
      trainings: mockTrainings,
      payments: mockPayments
    }))

  } catch (err: any) {
    console.error('Fetch players error:', err)
    error.value = err.response?.data?.message || t('dashboard.errorFetchingPlayers')
  } finally {
    loading.value = false
  }
}

// Toggle category filter
const toggleCategory = (categoryUuid: string) => {
  const index = selectedCategories.value.indexOf(categoryUuid)
  if (index > -1) {
    selectedCategories.value.splice(index, 1)
  } else {
    selectedCategories.value.push(categoryUuid)
  }
  fetchPlayers()
}

// Remove category filter
const removeCategory = (categoryUuid: string) => {
  const index = selectedCategories.value.indexOf(categoryUuid)
  if (index > -1) {
    selectedCategories.value.splice(index, 1)
    fetchPlayers()
  }
}

// Search
const handleSearch = () => {
  currentPage.value = 1
  fetchPlayers()
}

// Clear all filters
const clearAllFilters = () => {
  selectedCategories.value = []
  searchQuery.value = ''
  filterSex.value = ''
  filterRegisterStatus.value = ''
  filterMedicalExam.value = ''
  filterInsurance.value = ''
  filterPayments.value = ''
  filterBirthDateFrom.value = ''
  filterBirthDateTo.value = ''
  filterRegisterDateFrom.value = ''
  filterRegisterDateTo.value = ''
  fetchPlayers()
}

// Save filters from modal
const saveFilters = () => {
  showFilterModal.value = false
  currentPage.value = 1
  fetchPlayers()
}

// Bulk selection
const toggleSelectAll = () => {
  if (selectAllInSystem.value) {
    // If all in system selected, deselect all
    selectAllInSystem.value = false
    selectAll.value = false
    selectedPlayerUuids.value = []
  } else {
    // Toggle select all on current page
    selectAll.value = !selectAll.value
    if (selectAll.value) {
      selectedPlayerUuids.value = players.value.map(player => player.uuid)
    } else {
      selectedPlayerUuids.value = []
    }
  }
}

const togglePlayerSelection = (playerUuid: string) => {
  const index = selectedPlayerUuids.value.indexOf(playerUuid)
  if (index > -1) {
    selectedPlayerUuids.value.splice(index, 1)
    selectAll.value = false
    selectAllInSystem.value = false
  } else {
    selectedPlayerUuids.value.push(playerUuid)
    if (selectedPlayerUuids.value.length === players.value.length) {
      selectAll.value = true
    }
  }
}

const selectAllPlayersInSystem = () => {
  selectAllInSystem.value = true
  // In real implementation, this would select all players matching current filters
}

// Bulk actions
const bulkExport = () => {
  showExportModal.value = true
  showBulkActionsDropdown.value = false
  // Reset checkboxes (keep first_name and last_name always true)
  Object.keys(exportColumns.value).forEach(key => {
    if (key !== 'first_name' && key !== 'last_name') {
      exportColumns.value[key] = false
    }
  })
}

const closeExportModal = () => {
  showExportModal.value = false
}

const handleExport = async () => {
  if (!clubUuid.value) return

  exporting.value = true

  try {
    const selectedColumns = Object.entries(exportColumns.value)
      .filter(([_, selected]) => selected)
      .map(([key]) => key)

    const payload: any = {
      columns: selectedColumns
    }

    if (selectAllInSystem.value) {
      payload.use_filters = true
      payload.filters = buildFilters()
    } else {
      payload.player_uuids = selectedPlayerUuids.value
    }

    await playerService.bulkExport(clubUuid.value, payload)
    toastStore.success(t('dashboard.exportStarted'))

    closeExportModal()

    // Clear selection
    selectedPlayerUuids.value = []
    selectAll.value = false
    selectAllInSystem.value = false
  } catch (err: any) {
    console.error('Error exporting players:', err)
    const errorMessage = err.response?.data?.message || t('dashboard.errorExporting')
    toastStore.error(errorMessage)
  } finally {
    exporting.value = false
  }
}

const bulkSendMessage = () => {
  openSendMessageModal()
  showBulkActionsDropdown.value = false
}

const bulkAddPayment = () => {
  openAddPaymentModal()
  showBulkActionsDropdown.value = false
}

const bulkAddCategory = () => {
  openAddCategoryModal()
  showBulkActionsDropdown.value = false
}

const bulkRemoveCategory = () => {
  openRemoveCategoryModal()
  showBulkActionsDropdown.value = false
}

// Dropdowns
const toggleDropdown = (event: Event, playerUuid: string) => {
  event.stopPropagation()
  if (openDropdownId.value === playerUuid) {
    openDropdownId.value = null
  } else {
    openDropdownId.value = playerUuid
  }
}

const closeAllDropdowns = () => {
  openDropdownId.value = null
  showCategoryDropdown.value = false
  showBulkActionsDropdown.value = false
  showHeaderActionsDropdown.value = false
}

// Accordion
const toggleAccordion = (playerUuid: string) => {
  if (openAccordionId.value === playerUuid) {
    openAccordionId.value = null
  } else {
    openAccordionId.value = playerUuid
  }
}

// Copy to clipboard
const copyToClipboard = async (text: string) => {
  if (!text) {
    toastStore.error(t('dashboard.errorNothingToCopy'))
    return
  }
  try {
    await navigator.clipboard.writeText(text)
    toastStore.success(t('dashboard.copiedToClipboard'))
  } catch (err) {
    console.error('Copy error:', err)
    toastStore.error(t('dashboard.errorCopying'))
  }
}

// Navigate to player detail
const goToPlayerDetail = (playerUuid: string) => {
  router.push(`/players/${playerUuid}`)
}

// Navigate to edit player
const goToEditPlayer = (playerUuid: string) => {
  router.push(`/players/${playerUuid}/edit`)
}

// Navigate to add player
const goToAddPlayer = () => {
  router.push('/players/create')
}

// Delete player handlers
const openDeleteModal = (playerUuid: string, playerName: string) => {
  playerToDelete.value = { uuid: playerUuid, name: playerName }
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  playerToDelete.value = null
}

const handleDelete = async () => {
  if (!playerToDelete.value || !clubUuid.value) return

  deleting.value = true

  try {
    const response = await playerService.deletePlayer(clubUuid.value, playerToDelete.value.uuid)
    const message = response?.message || t('dashboard.playerDeleted')
    toastStore.success(message)

    closeDeleteModal()
    fetchPlayers()
  } catch (err: any) {
    console.error('Error deleting player:', err)
    const errorMessage = err.response?.data?.message || t('dashboard.errorDeletingPlayer')
    toastStore.error(errorMessage)
  } finally {
    deleting.value = false
  }
}

// Add category modal handlers
const openAddCategoryModal = () => {
  showAddCategoryModal.value = true
  selectedCategoryToAdd.value = ''
}

const closeAddCategoryModal = () => {
  showAddCategoryModal.value = false
  selectedCategoryToAdd.value = ''
}

const handleAddCategory = async () => {
  if (!selectedCategoryToAdd.value || !clubUuid.value) return

  addingCategory.value = true

  try {
    const payload: any = {
      category_uuid: selectedCategoryToAdd.value
    }

    if (selectAllInSystem.value) {
      // Send filters to backend
      payload.use_filters = true
      payload.filters = buildFilters()
    } else {
      // Send selected player UUIDs
      payload.player_uuids = selectedPlayerUuids.value
    }

    const response = await playerService.bulkAddCategory(clubUuid.value, payload)
    const message = response?.message || t('dashboard.categoryAddedToPlayers')
    toastStore.success(message)

    closeAddCategoryModal()

    // Clear selection
    selectedPlayerUuids.value = []
    selectAll.value = false
    selectAllInSystem.value = false

    // Refresh players
    fetchPlayers()
  } catch (err: any) {
    console.error('Error adding category to players:', err)
    const errorMessage = err.response?.data?.message || t('dashboard.errorAddingCategory')
    toastStore.error(errorMessage)
  } finally {
    addingCategory.value = false
  }
}

// Build filters for API request
const buildFilters = () => {
  const filters: any = {}

  if (searchQuery.value) {
    filters.search = searchQuery.value
  }

  if (selectedCategories.value.length > 0) {
    filters.categories = selectedCategories.value
  }

  if (filterSex.value) {
    filters.gender = filterSex.value
  }

  if (filterRegisterStatus.value) {
    filters.register_status = filterRegisterStatus.value
  }

  if (filterBirthDateFrom.value) {
    filters.birth_date_from = filterBirthDateFrom.value
  }

  if (filterBirthDateTo.value) {
    filters.birth_date_to = filterBirthDateTo.value
  }

  if (filterRegisterDateFrom.value) {
    filters.register_date_from = filterRegisterDateFrom.value
  }

  if (filterRegisterDateTo.value) {
    filters.register_date_to = filterRegisterDateTo.value
  }

  if (sortField.value) {
    filters.sort = sortField.value
    filters.sort_type = sortDirection.value
  }

  return filters
}

// Remove category modal handlers
const openRemoveCategoryModal = () => {
  showRemoveCategoryModal.value = true
  selectedCategoryToRemove.value = ''
}

const closeRemoveCategoryModal = () => {
  showRemoveCategoryModal.value = false
  selectedCategoryToRemove.value = ''
}

const handleRemoveCategory = async () => {
  if (!selectedCategoryToRemove.value || !clubUuid.value) return

  removingCategory.value = true

  try {
    const payload: any = {
      category_uuid: selectedCategoryToRemove.value
    }

    if (selectAllInSystem.value) {
      // Send filters to backend
      payload.use_filters = true
      payload.filters = buildFilters()
    } else {
      // Send selected player UUIDs
      payload.player_uuids = selectedPlayerUuids.value
    }

    const response = await playerService.bulkRemoveCategory(clubUuid.value, payload)
    const message = response?.message || t('dashboard.categoryRemovedFromPlayers')
    toastStore.warning(message)

    closeRemoveCategoryModal()

    // Clear selection
    selectedPlayerUuids.value = []
    selectAll.value = false
    selectAllInSystem.value = false

    // Refresh players
    fetchPlayers()
  } catch (err: any) {
    console.error('Error removing category from players:', err)
    const errorMessage = err.response?.data?.message || t('dashboard.errorRemovingCategory')
    toastStore.error(errorMessage)
  } finally {
    removingCategory.value = false
  }
}

// Send message modal handlers
const openSendMessageModal = () => {
  showSendMessageModal.value = true
  messageText.value = ''
  sendEmailWithMessage.value = false
}

const closeSendMessageModal = () => {
  showSendMessageModal.value = false
  messageText.value = ''
  sendEmailWithMessage.value = false
}

const handleSendMessage = async () => {
  if (!messageText.value.trim() || !clubUuid.value) return

  sendingMessage.value = true

  try {
    const payload: any = {
      message: messageText.value.trim(),
      send_email: sendEmailWithMessage.value
    }

    if (selectAllInSystem.value) {
      // Send filters to backend
      payload.use_filters = true
      payload.filters = buildFilters()
    } else {
      // Send selected player UUIDs
      payload.player_uuids = selectedPlayerUuids.value
    }

    const response = await playerService.bulkSendMessage(clubUuid.value, payload)
    const message = response?.message || t('dashboard.messageSentToPlayers')
    toastStore.success(message)

    closeSendMessageModal()

    // Clear selection
    selectedPlayerUuids.value = []
    selectAll.value = false
    selectAllInSystem.value = false
  } catch (err: any) {
    console.error('Error sending message to players:', err)
    const errorMessage = err.response?.data?.message || t('dashboard.errorSendingMessage')
    toastStore.error(errorMessage)
  } finally {
    sendingMessage.value = false
  }
}

// Add payment modal handlers
const openAddPaymentModal = async () => {
  showAddPaymentModal.value = true
  paymentName.value = ''
  paymentAmount.value = null
  paymentDaysToPay.value = null
  paymentBankAccountUuid.value = ''
  paymentDescription.value = ''

  // Fetch bank accounts
  if (clubUuid.value) {
    try {
      const response = await clubService.getBankAccounts(clubUuid.value)
      bankAccounts.value = response.bank_accounts || []
    } catch (err: any) {
      console.error('Error fetching bank accounts:', err)
      toastStore.error(t('dashboard.errorFetchingBankAccounts'))
    }
  }
}

const closeAddPaymentModal = () => {
  showAddPaymentModal.value = false
  paymentName.value = ''
  paymentAmount.value = null
  paymentDaysToPay.value = null
  paymentBankAccountUuid.value = ''
  paymentDescription.value = ''
}

const handleAddPayment = async () => {
  if (!paymentName.value.trim() || !paymentAmount.value || !paymentDaysToPay.value || !paymentBankAccountUuid.value || !clubUuid.value) return

  addingPayment.value = true

  try {
    const payload: any = {
      name: paymentName.value.trim(),
      amount: paymentAmount.value,
      days_to_pay: paymentDaysToPay.value,
      bank_account_uuid: paymentBankAccountUuid.value,
      description: paymentDescription.value.trim() || null
    }

    if (selectAllInSystem.value) {
      // Send filters to backend
      payload.use_filters = true
      payload.filters = buildFilters()
    } else {
      // Send selected player UUIDs
      payload.player_uuids = selectedPlayerUuids.value
    }

    const response = await playerService.bulkAddPayment(clubUuid.value, payload)
    const message = response?.message || t('dashboard.paymentAddedToPlayers')
    toastStore.success(message)

    closeAddPaymentModal()

    // Clear selection
    selectedPlayerUuids.value = []
    selectAll.value = false
    selectAllInSystem.value = false
  } catch (err: any) {
    console.error('Error adding payment to players:', err)
    const errorMessage = err.response?.data?.message || t('dashboard.errorAddingPayment')
    toastStore.error(errorMessage)
  } finally {
    addingPayment.value = false
  }
}

// Check if dropdown should open upwards (for last 3 items)
const shouldOpenUpwards = (index: number): boolean => {
  return index >= players.value.length - 3
}

// Format date to YYYY-MM-DD
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Get category name
const getCategoryName = (categoryUuid: string): string => {
  const category = categories.value.find((cat: any) => cat.uuid === categoryUuid)
  return category?.name || categoryUuid
}

// Pagination handlers
const changePage = (page: number) => {
  currentPage.value = page
  // Reset selection when changing page (unless all in system selected)
  if (!selectAllInSystem.value) {
    selectedPlayerUuids.value = []
    selectAll.value = false
  }
  fetchPlayers()
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

const changePerPage = () => {
  currentPage.value = 1
  // Reset selection when changing per page (unless all in system selected)
  if (!selectAllInSystem.value) {
    selectedPlayerUuids.value = []
    selectAll.value = false
  }
  fetchPlayers()
}

// Computed
const hasActiveFilters = computed(() => {
  return selectedCategories.value.length > 0 ||
    searchQuery.value.trim() !== '' ||
    filterSex.value !== '' ||
    filterRegisterStatus.value !== '' ||
    filterBirthDateFrom.value !== '' ||
    filterBirthDateTo.value !== '' ||
    filterRegisterDateFrom.value !== '' ||
    filterRegisterDateTo.value !== ''
})

const selectedCount = computed(() => {
  return selectAllInSystem.value ? total.value : selectedPlayerUuids.value.length
})

const showBulkActions = computed(() => selectedPlayerUuids.value.length > 0 || selectAllInSystem.value)

// Calculate visible page numbers
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Watch for click outside
onMounted(() => {
  document.addEventListener('click', closeAllDropdowns)
  fetchCategories()
  fetchApplicationsCount()
  fetchPlayers()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen" @click="closeAllDropdowns">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2 flex justify-between items-center">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('dashboard.title') }} <span class="text-sm font-normal text-gray-600">({{ t('dashboard.found') }} {{ total }})</span>
        </h1>
        <div class="flex flex-col items-end gap-1">
          <div class="flex items-center gap-2">
          <button
            v-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'"
            @click="goToAddPlayer"
            class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            <span class="md:inline hidden">{{ t('dashboard.addPlayer') }}</span>
            <span class="md:hidden inline">{{ t('dashboard.addPlayerShort') }}</span>
          </button>
          <div v-if="canImport" class="relative">
            <button
              @click.stop="showHeaderActionsDropdown = !showHeaderActionsDropdown"
              class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-white text-sm md:text-sm text-xs font-medium text-gray-700 rounded hover:bg-gray-100 border-2 border-gray-300 flex items-center gap-2 md:gap-2 gap-1"
            >
              <span>{{ t('common.actions') }}</span>
              <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div
              v-show="showHeaderActionsDropdown"
              class="absolute right-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded shadow-lg z-50"
              @click.stop
            >
              <button
                @click="openImportModal"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                </svg>
                {{ t('playerImport.importFromFile') }}
              </button>
            </div>
          </div>
          </div>
          <router-link
            v-if="canSeeApplications && (currentRole === 'OWNER' || currentRole === 'SUPERVISOR' || currentRole === 'COACH')"
            to="/club-applications"
            class="text-sm md:text-sm text-xs text-blue-600 hover:text-blue-800 hover:underline"
            :class="{ 'font-semibold': applicationsCount > 0 }"
          >
            {{ t('clubApplications.dashboardLink', { count: applicationsCount }) }}
          </router-link>
        </div>
      </div>

      <!-- Filters -->
      <div class="mb-4 flex flex-wrap justify-between items-center gap-2">
        <div class="flex gap-2">
          <!-- Category Filter Dropdown -->
          <div class="relative inline-block">
            <button
              @click.stop="showCategoryDropdown = !showCategoryDropdown"
              class="px-3 py-1.5 md:px-3 md:py-1.5 px-2 py-1 bg-white text-sm md:text-sm text-xs font-medium text-gray-700 rounded hover:bg-gray-50 border-2 border-gray-300"
            >
              {{ t('dashboard.selectCategories') }}
              <svg class="inline-block w-3.5 h-3.5 md:w-3.5 md:h-3.5 w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div
              v-show="showCategoryDropdown"
              class="absolute left-0 mt-2 w-48 bg-white border-2 border-gray-300 rounded shadow-md z-50"
              @click.stop
            >
              <div class="p-2">
                <label
                  v-for="category in categories"
                  :key="category.uuid"
                  class="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :value="category.uuid"
                    :checked="selectedCategories.includes(category.uuid)"
                    @change="toggleCategory(category.uuid)"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  >
                  <span class="text-sm text-gray-700">{{ category.name }}</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Advanced Filters Button -->
          <button
            @click="showFilterModal = true"
            class="px-3 py-1.5 md:px-3 md:py-1.5 px-2 py-1 bg-white text-sm md:text-sm text-xs font-medium text-gray-700 rounded hover:bg-gray-50 border-2 border-gray-300 flex items-center gap-1 md:gap-1.5"
          >
            <svg class="w-3.5 h-3.5 md:w-3.5 md:h-3.5 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd"/>
            </svg>
            <span class="hidden sm:inline">{{ t('dashboard.playerFilters') }}</span>
            <span class="sm:hidden">{{ t('dashboard.filtersShort') }}</span>
          </button>
        </div>

        <!-- Search -->
        <div class="flex ml-auto">
          <input
            v-model="searchQuery"
            @keyup.enter="handleSearch"
            type="text"
            :placeholder="t('dashboard.search')"
            class="w-40 md:w-40 w-32 px-2.5 py-1.5 md:px-2.5 md:py-1.5 px-2 py-1 text-sm md:text-sm text-xs border-2 border-r-0 border-gray-300 rounded-l focus:outline-none focus:border-blue-600"
          >
          <button
            @click="handleSearch"
            class="px-2.5 py-1.5 md:px-2.5 md:py-1.5 px-2 py-1 bg-blue-600 text-white rounded-r hover:bg-blue-700 border-2 border-blue-600"
          >
            <svg class="w-3.5 h-3.5 md:w-3.5 md:h-3.5 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Active Filters -->
      <div v-if="hasActiveFilters" class="mb-4 flex flex-wrap gap-2">
        <div
          v-for="categoryUuid in selectedCategories"
          :key="categoryUuid"
          class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300"
        >
          <button @click="removeCategory(categoryUuid)" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('dashboard.category') }} {{ getCategoryName(categoryUuid) }}</span>
        </div>

        <div v-if="searchQuery" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="searchQuery = ''; handleSearch()" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('dashboard.searchQuery') }} "{{ searchQuery }}"</span>
        </div>

        <div v-if="filterSex" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="filterSex = ''; fetchPlayers()" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('dashboard.gender') }} {{ filterSex === 'M' ? t('dashboard.genderMale') : t('dashboard.genderFemale') }}</span>
        </div>

        <div v-if="filterRegisterStatus" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="filterRegisterStatus = ''; fetchPlayers()" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('dashboard.status') }} {{ filterRegisterStatus === 'ACTIVE' ? t('dashboard.statusActive') : t('dashboard.statusInactive') }}</span>
        </div>

        <div v-if="filterBirthDateFrom || filterBirthDateTo" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="filterBirthDateFrom = ''; filterBirthDateTo = ''; fetchPlayers()" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('dashboard.birthDate') }} {{ filterBirthDateFrom || '...' }} - {{ filterBirthDateTo || '...' }}</span>
        </div>

        <div v-if="filterRegisterDateFrom || filterRegisterDateTo" class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300">
          <button @click="filterRegisterDateFrom = ''; filterRegisterDateTo = ''; fetchPlayers()" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('dashboard.registration') }} {{ filterRegisterDateFrom || '...' }} - {{ filterRegisterDateTo || '...' }}</span>
        </div>

        <button
          @click="clearAllFilters"
          class="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 font-medium"
        >
          {{ t('dashboard.clearAllFilters') }}
        </button>
      </div>

      <!-- Filter Modal -->
      <div
        v-if="showFilterModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        @click="showFilterModal = false"
      >
        <div
          class="bg-white rounded shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          @click.stop
        >
          <div class="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900">{{ t('dashboard.playerFilters') }}</h2>
            <button
              @click="showFilterModal = false"
              class="text-gray-500 hover:text-gray-700"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>

          <div class="p-4 space-y-4">
            <!-- Płeć -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('dashboard.genderShort') }}</label>
              <select
                v-model="filterSex"
                class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
              >
                <option value="">{{ t('dashboard.all') }}</option>
                <option value="M">{{ t('dashboard.genderMale') }}</option>
                <option value="W">{{ t('dashboard.genderFemale') }}</option>
              </select>
            </div>

            <!-- Status rejestracji -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('dashboard.registrationStatus') }}</label>
              <select
                v-model="filterRegisterStatus"
                class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
              >
                <option value="">{{ t('dashboard.all') }}</option>
                <option value="ACTIVE">{{ t('dashboard.statusActive') }}</option>
                <option value="NOT_ACTIVE">{{ t('dashboard.statusInactive') }}</option>
              </select>
            </div>

            <!-- Data urodzenia -->
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('dashboard.birthDateFrom') }}</label>
                <input
                  v-model="filterBirthDateFrom"
                  type="date"
                  class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('dashboard.birthDateTo') }}</label>
                <input
                  v-model="filterBirthDateTo"
                  type="date"
                  class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
            </div>

            <!-- Data rejestracji -->
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('dashboard.registrationDateFrom') }}</label>
                <input
                  v-model="filterRegisterDateFrom"
                  type="date"
                  class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('dashboard.registrationDateTo') }}</label>
                <input
                  v-model="filterRegisterDateTo"
                  type="date"
                  class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
            </div>
          </div>

          <div class="p-4 border-t border-gray-200 flex justify-end gap-2">
            <button
              @click="showFilterModal = false"
              class="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded hover:bg-gray-200 border border-gray-300"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              @click="saveFilters"
              class="px-4 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700"
            >
              {{ t('dashboard.applyFilters') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Bulk Actions Bar -->
      <div
        v-if="showBulkActions"
        class="mb-4 flex flex-col gap-3 bg-blue-50 border-2 border-blue-300 rounded p-3"
      >
        <div class="flex items-center gap-3">
          <div class="relative">
            <button
              @click.stop="showBulkActionsDropdown = !showBulkActionsDropdown"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <span>{{ t('dashboard.optionsForSelected') }} ({{ selectedCount }})</span>
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div
              v-show="showBulkActionsDropdown"
              class="absolute left-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded shadow-lg z-50"
              @click.stop
            >
              <button
                @click="bulkExport"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
                {{ t('dashboard.exportSelected') }}
              </button>
              <button
                @click="bulkSendMessage"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                {{ t('dashboard.sendMessage') }}
              </button>
              <button
                v-if="currentRole !== 'COACH'"
                @click="bulkAddPayment"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                  <path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd"/>
                </svg>
                {{ t('dashboard.addPayment') }}
              </button>
              <button
                v-if="currentRole !== 'COACH'"
                @click="bulkAddCategory"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd"/>
                </svg>
                {{ t('dashboard.addCategory') }}
              </button>
              <button
                v-if="currentRole !== 'COACH'"
                @click="bulkRemoveCategory"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
                </svg>
                {{ t('dashboard.removeCategory') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Select All Banner -->
        <div
          v-if="selectAll && !selectAllInSystem && total > players.length"
          class="flex flex-col gap-2 text-sm"
        >
          <span class="text-gray-700">{{ t('dashboard.allInTableSelected') }}</span>
          <button
            @click="selectAllPlayersInSystem"
            class="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 self-start"
          >
            {{ t('dashboard.selectAllInSystem', { total: total }) }}
          </button>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 bg-red-50 border-2 border-red-300 rounded p-3">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-sm text-gray-600">{{ t('dashboard.loading') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-if="!loading && players.length > 0" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-y-visible" style="overflow-x: clip;">
        <table class="w-full text-sm">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-3 py-2 text-left">
                <input
                  type="checkbox"
                  :checked="selectAll || selectAllInSystem"
                  @change="toggleSelectAll"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                >
              </th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700 hidden lg:table-cell">{{ t('dashboard.licenseNumber') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                <div class="flex items-center gap-1">
                  <span>{{ t('dashboard.nameAndSurname') }}</span>
                  <div class="flex flex-col">
                    <button
                      @click.stop="sortField = 'last_name'; sortDirection = 'ASC'; fetchPlayers()"
                      :class="sortField === 'last_name' && sortDirection === 'ASC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"/>
                      </svg>
                    </button>
                    <button
                      @click.stop="sortField = 'last_name'; sortDirection = 'DESC'; fetchPlayers()"
                      :class="sortField === 'last_name' && sortDirection === 'DESC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                <div class="flex items-center gap-1">
                  <span>{{ t('dashboard.genderShort') }}</span>
                  <div class="flex flex-col">
                    <button
                      @click.stop="sortField = 'gender'; sortDirection = 'ASC'; fetchPlayers()"
                      :class="sortField === 'gender' && sortDirection === 'ASC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"/>
                      </svg>
                    </button>
                    <button
                      @click.stop="sortField = 'gender'; sortDirection = 'DESC'; fetchPlayers()"
                      :class="sortField === 'gender' && sortDirection === 'DESC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('dashboard.categories') }}</th>
              <!-- <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('dashboard.trainings') }}</th> -->
              <!-- <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('dashboard.payments') }}</th> -->
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700 hidden lg:table-cell">
                <div class="flex items-center gap-1">
                  <span>{{ t('dashboard.birthDate') }}</span>
                  <div class="flex flex-col">
                    <button
                      @click.stop="sortField = 'birth_date'; sortDirection = 'ASC'; fetchPlayers()"
                      :class="sortField === 'birth_date' && sortDirection === 'ASC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"/>
                      </svg>
                    </button>
                    <button
                      @click.stop="sortField = 'birth_date'; sortDirection = 'DESC'; fetchPlayers()"
                      :class="sortField === 'birth_date' && sortDirection === 'DESC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700 hidden lg:table-cell">
                <div class="flex items-center gap-1">
                  <span>{{ t('dashboard.registration') }}</span>
                  <div class="flex flex-col">
                    <button
                      @click.stop="sortField = 'created_at'; sortDirection = 'ASC'; fetchPlayers()"
                      :class="sortField === 'created_at' && sortDirection === 'ASC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"/>
                      </svg>
                    </button>
                    <button
                      @click.stop="sortField = 'created_at'; sortDirection = 'DESC'; fetchPlayers()"
                      :class="sortField === 'created_at' && sortDirection === 'DESC' ? 'text-gray-900' : 'text-gray-400 opacity-60 hover:text-gray-700'"
                    >
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </th>
              <th v-if="canSeeActions" class="px-2 py-2 text-right text-xs font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(player, index) in players"
              :key="player.id"
              class="border-b border-gray-200 hover:bg-gray-50"
            >
              <td class="px-3 py-2">
                <input
                  type="checkbox"
                  :checked="selectedPlayerUuids.includes(player.uuid)"
                  @change="togglePlayerSelection(player.uuid)"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                >
              </td>
              <td class="px-3 py-2 text-gray-900 hidden lg:table-cell">{{ player.license_number || '-' }}</td>
              <td class="px-3 py-2">
                <a
                  :href="`/players/${player.uuid}`"
                  @click.prevent="goToPlayerDetail(player.uuid)"
                  class="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {{ player.last_name }} {{ player.first_name }}
                </a>
                <div class="text-xs text-gray-600 mt-0.5 lg:hidden">{{ formatDate(player.birth_date) }}</div>
              </td>
              <td class="px-3 py-2">
                <span
                  v-if="player.gender === 'M'"
                  class="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded"
                >
                  M
                </span>
                <span
                  v-else
                  class="inline-block px-2 py-0.5 bg-pink-100 text-pink-700 text-[10px] font-medium rounded"
                >
                  K
                </span>
              </td>
              <td class="px-3 py-2">
                <div class="flex flex-wrap gap-1">
                  <template v-for="category in player.categories" :key="category.uuid">
                    <router-link
                      v-if="isCategoryAccessible(category.uuid)"
                      :to="`/categories/${category.uuid}`"
                      class="inline-block px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] rounded cursor-pointer hover:bg-gray-300 transition-colors"
                    >
                      {{ category.name }}
                    </router-link>
                    <span
                      v-else
                      class="inline-block px-2 py-0.5 bg-gray-200 text-gray-500 text-[10px] rounded opacity-60"
                    >
                      {{ category.name }}
                    </span>
                  </template>
                </div>
              </td>
              <!-- <td class="px-3 py-2">
                <div class="flex gap-1">
                  <span
                    v-for="(training, idx) in player.trainings"
                    :key="idx"
                    :class="{
                      'w-2 h-2 rounded-full': true,
                      'bg-green-500': training.color === 'green',
                      'bg-red-500': training.color === 'red'
                    }"
                    :title="training.date"
                  ></span>
                </div>
              </td> -->
              <!-- <td class="px-3 py-2">
                <div class="flex gap-1">
                  <span
                    v-for="(payment, idx) in player.payments"
                    :key="idx"
                    :class="{
                      'w-2 h-2 rounded-full': true,
                      'bg-green-500': payment.color === 'green',
                      'bg-red-500': payment.color === 'red'
                    }"
                    :title="payment.date"
                  ></span>
                </div>
              </td> -->
              <td class="px-3 py-2 text-gray-900 hidden lg:table-cell">{{ formatDate(player.birth_date) }}</td>
              <td class="px-3 py-2 hidden lg:table-cell">
                <div v-if="player.registration_status !== 'registered'" class="flex items-center gap-1">
                  <span class="text-gray-900">{{ formatDate(player.created_at) }}</span>
                  <span class="w-2 h-2 bg-red-600 rounded-full"></span>
                </div>
                <span v-else class="text-gray-900">{{ formatDate(player.created_at) }}</span>
              </td>
              <td class="px-2 py-2 sticky right-0 bg-inherit" style="position: relative;">
                <div class="flex items-center gap-1 justify-end">
                  <div v-if="player.phone" class="relative">
                    <button
                      @click="copyToClipboard(player.phone)"
                      class="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                      :data-contact-tooltip="`Tel: ${player.phone}`"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                      </svg>
                    </button>
                  </div>
                  <div v-if="player.email" class="relative">
                    <button
                      @click="copyToClipboard(player.email)"
                      class="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                      :data-contact-tooltip="`Email: ${player.email}`"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                      </svg>
                    </button>
                  </div>
                  <button
                    v-if="canSeeActions"
                    @click="toggleDropdown($event, player.uuid)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('dashboard.actions') }}
                  </button>
                  <div
                    v-if="canSeeActions"
                    :class="[
                      'custom-dropdown',
                      'dropdown-bottom',
                      { 'show': openDropdownId === player.uuid }
                    ]"
                  >
                    <router-link v-if="canShow && (currentRole === 'OWNER' || currentRole === 'SUPERVISOR' || currentRole === 'COACH')" :to="`/players/${player.uuid}`">{{ t('dashboard.view') }}</router-link>
                    <router-link v-if="canUpdate && (currentRole === 'OWNER' || currentRole === 'SUPERVISOR')" :to="`/players/${player.uuid}/edit`">{{ t('common.edit') }}</router-link>
                    <button v-if="canDelete && (currentRole === 'OWNER' || currentRole === 'SUPERVISOR')" class="delete-action" @click="openDeleteModal(player.uuid, `${player.last_name} ${player.first_name}`)">{{ t('common.delete') }}</button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Accordion -->
      <div v-if="!loading && players.length > 0" class="md:hidden space-y-2">
        <div
          v-for="player in players"
          :key="player.id"
          class="bg-white border-2 border-gray-300 rounded shadow-sm"
        >
          <!-- Accordion Header -->
          <div class="flex items-center gap-2 p-3">
            <input
              type="checkbox"
              :checked="selectedPlayerUuids.includes(player.uuid)"
              @change.stop="togglePlayerSelection(player.uuid)"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded flex-shrink-0"
            >
            <div
              @click="toggleAccordion(player.uuid)"
              class="flex items-center justify-between flex-1 cursor-pointer"
            >
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <span
                    v-if="player.gender === 'M'"
                    class="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded"
                  >M</span>
                  <span
                    v-else
                    class="inline-block px-2 py-0.5 bg-pink-100 text-pink-700 text-[10px] font-medium rounded"
                  >K</span>
                  <a
                    :href="`/players/${player.uuid}`"
                    @click.stop.prevent="goToPlayerDetail(player.uuid)"
                    class="text-sm text-blue-600 hover:underline"
                  >
                    {{ player.last_name }} {{ player.first_name }}
                  </a>
                </div>
                <div class="flex items-center gap-2 mt-1">
                  <!-- <div class="hidden min-[500px]:flex items-center gap-1">
                    <span class="text-[10px] text-gray-600">{{ t('dashboard.trainings') }}:</span>
                    <div class="flex gap-1">
                      <span
                        v-for="(training, idx) in player.trainings"
                        :key="idx"
                        :class="{
                          'w-3 h-3 rounded-full': true,
                          'bg-green-600': training.color === 'green',
                          'bg-red-600': training.color === 'red'
                        }"
                        :title="training.date"
                      ></span>
                    </div>
                  </div> -->
                  <div v-if="player.categories && player.categories.length > 0" class="flex gap-1 flex-wrap">
                    <template v-for="category in player.categories" :key="category.uuid">
                      <router-link
                        v-if="isCategoryAccessible(category.uuid)"
                        :to="`/categories/${category.uuid}`"
                        @click.stop
                        class="inline-block px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] rounded cursor-pointer hover:bg-gray-300 transition-colors"
                      >
                        {{ category.name }}
                      </router-link>
                      <span
                        v-else
                        class="inline-block px-2 py-0.5 bg-gray-200 text-gray-500 text-[10px] rounded opacity-60"
                      >
                        {{ category.name }}
                      </span>
                    </template>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2 ml-2">
                <span v-if="player.registration_status !== 'registered'" class="w-2 h-2 bg-red-600 rounded-full lg:hidden"></span>
                <div v-if="canSeeActions" style="position: relative;">
                  <button
                    @click.stop="toggleDropdown($event, player.uuid)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('dashboard.actions') }}
                  </button>
                  <div
                    :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === player.uuid }]"
                  >
                    <button
                      v-if="player.phone"
                      @click.stop="copyToClipboard(player.phone)"
                      class="flex items-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                      </svg>
                      {{ t('dashboard.copyPhone') }}
                    </button>
                    <button
                      v-if="player.email"
                      @click.stop="copyToClipboard(player.email)"
                      class="flex items-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                      </svg>
                      {{ t('dashboard.copyEmail') }}
                    </button>
                    <router-link v-if="canShow" :to="`/players/${player.uuid}`">{{ t('dashboard.view') }}</router-link>
                    <router-link v-if="canUpdate && (currentRole === 'OWNER' || currentRole === 'SUPERVISOR')" :to="`/players/${player.uuid}/edit`">{{ t('common.edit') }}</router-link>
                    <button v-if="canDelete && (currentRole === 'OWNER' || currentRole === 'SUPERVISOR')" class="delete-action" @click.stop="openDeleteModal(player.uuid, `${player.last_name} ${player.first_name}`)">{{ t('common.delete') }}</button>
                  </div>
                </div>
                <svg
                  :class="{
                    'w-5 h-5 text-gray-600 transition-transform': true,
                    'transform rotate-180': openAccordionId === player.uuid
                  }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Accordion Content -->
          <div v-show="openAccordionId === player.uuid" class="border-t-2 border-gray-300 p-3 space-y-2 text-sm">
            <!-- Trainings for mobile < 500px -->
            <!-- <div class="max-[499px]:block hidden mb-2">
              <span class="text-gray-600 text-xs">{{ t('dashboard.trainings') }}:</span>
              <div class="flex gap-1 mt-1">
                <span
                  v-for="(training, idx) in player.trainings"
                  :key="idx"
                  :class="{
                    'w-3 h-3 rounded-full': true,
                    'bg-green-600': training.color === 'green',
                    'bg-red-600': training.color === 'red'
                  }"
                  :title="training.date"
                ></span>
              </div>
            </div> -->

            <!-- Payments for mobile < 500px -->
            <!-- <div class="max-[499px]:block hidden mb-2">
              <span class="text-gray-600 text-xs">{{ t('dashboard.payments') }}:</span>
              <div class="flex gap-1 mt-1">
                <span
                  v-for="(payment, idx) in player.payments"
                  :key="idx"
                  :class="{
                    'w-3 h-3 rounded-full': true,
                    'bg-green-600': payment.color === 'green',
                    'bg-red-600': payment.color === 'red'
                  }"
                  :title="payment.date"
                ></span>
              </div>
            </div> -->

            <!-- Categories for mobile < 500px -->
            <div v-if="player.categories && player.categories.length > 0" class="max-[499px]:block hidden mb-2">
              <span class="text-gray-600 text-xs">{{ t('dashboard.categories') }}:</span>
              <div class="flex gap-1 mt-1 flex-wrap">
                <template v-for="category in player.categories" :key="category.uuid">
                  <router-link
                    v-if="isCategoryAccessible(category.uuid)"
                    :to="`/categories/${category.uuid}`"
                    class="inline-block px-2 py-0.5 bg-gray-200 text-gray-700 text-[10px] rounded cursor-pointer hover:bg-gray-300 transition-colors"
                  >
                    {{ category.name }}
                  </router-link>
                  <span
                    v-else
                    class="inline-block px-2 py-0.5 bg-gray-200 text-gray-500 text-[10px] rounded opacity-60"
                  >
                    {{ category.name }}
                  </span>
                </template>
              </div>
            </div>

            <!-- Two column grid -->
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span class="text-gray-600 text-xs">{{ t('dashboard.phoneNumber') }}</span>
                <div class="flex items-center gap-1">
                  <div class="text-gray-900 text-xs">{{ player.phone || '-' }}</div>
                  <button
                    v-if="player.phone"
                    @click="copyToClipboard(player.phone)"
                    class="p-1 text-blue-600 hover:text-blue-800"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                <span class="text-gray-600 text-xs">{{ t('dashboard.email') }}</span>
                <div class="flex items-center gap-1">
                  <div class="text-gray-900 text-xs truncate">{{ player.email || '-' }}</div>
                  <button
                    v-if="player.email"
                    @click="copyToClipboard(player.email)"
                    class="p-1 text-blue-600 hover:text-blue-800 flex-shrink-0"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                <span class="text-gray-600 text-xs">{{ t('dashboard.licenseNumber') }}:</span>
                <div class="text-gray-900 text-xs">{{ player.license_number || '-' }}</div>
              </div>
              <div>
                <span class="text-gray-600 text-xs">{{ t('dashboard.birthDate') }}</span>
                <div class="text-gray-900 text-xs">{{ player.birth_date ? formatDate(player.birth_date) : '-' }}</div>
              </div>
              <div>
                <span class="text-gray-600 text-xs">{{ t('dashboard.guardian') }}</span>
                <div class="text-gray-900 text-xs">
                  {{ player.guardians && player.guardians.length > 0 ? `${player.guardians[0].first_name} ${player.guardians[0].last_name}` : '-' }}
                </div>
              </div>
              <div>
                <span class="text-gray-600 text-xs">{{ t('dashboard.medicalExamDate') }}</span>
                <div class="flex items-center gap-1">
                  <template v-if="player.medical_exam_date">
                    <template v-if="new Date(player.medical_exam_date) > new Date()">
                      <span class="w-2 h-2 bg-green-600 rounded-full"></span>
                      <span class="text-gray-900 text-xs">{{ formatDate(player.medical_exam_date) }}</span>
                    </template>
                    <template v-else>
                      <span class="w-2 h-2 bg-red-600 rounded-full"></span>
                      <span class="text-gray-900 line-through text-xs">{{ formatDate(player.medical_exam_date) }}</span>
                    </template>
                  </template>
                  <span v-else class="text-gray-900 text-xs">-</span>
                </div>
              </div>
              <!-- <div>
                <span class="text-gray-600 text-xs">{{ t('dashboard.insurance') }}</span>
                <div class="flex items-center gap-1">
                  <span class="w-2 h-2 bg-green-600 rounded-full"></span>
                  <span class="text-gray-900 text-xs">01.01.2026</span>
                </div>
              </div> -->
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && players.length === 0" class="text-center py-12 bg-white border-2 border-gray-300 rounded">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">{{ t('dashboard.noPlayers') }}</h3>
        <p class="mt-1 text-sm text-gray-500">
          {{ hasActiveFilters ? t('dashboard.noPlayersFiltered') : t('dashboard.noPlayersYet') }}
        </p>
        <div class="mt-6">
          <button
            v-if="hasActiveFilters"
            @click="clearAllFilters"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
          >
            {{ t('dashboard.clearFilters') }}
          </button>
          <button
            v-else-if="currentRole === 'OWNER' || currentRole === 'SUPERVISOR'"
            @click="goToAddPlayer"
            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg class="mr-2 -ml-1 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            {{ t('dashboard.addPlayer') }}
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && players.length > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4 bg-white border-2 border-gray-300 rounded p-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('dashboard.displaying') }} {{ fromItem }} {{ t('dashboard.to') }} {{ toItem }} {{ t('dashboard.of') }} {{ total }} {{ t('dashboard.playersWord') }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('dashboard.playersPerPage') }}</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeDeleteModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('dashboard.deleteConfirmation') }}</h2>
        <p class="text-gray-700 mb-6">
          {{ t('dashboard.deletePlayerConfirm') }} "<span class="font-semibold">{{ playerToDelete?.name }}</span>"?
        </p>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="deleting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleDelete"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="deleting"
          >
            {{ deleting ? t('dashboard.deleting') : t('common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Category Modal -->
  <div
    v-if="showAddCategoryModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeAddCategoryModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('dashboard.addCategoryToSelected') }}</h2>

        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ t('dashboard.selectCategory') }}
          </label>
          <select
            v-model="selectedCategoryToAdd"
            class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-500"
          >
            <option value="">{{ t('dashboard.chooseCategory') }}</option>
            <option
              v-for="category in categories"
              :key="category.uuid"
              :value="category.uuid"
            >
              {{ category.name }}
            </option>
          </select>
        </div>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeAddCategoryModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="addingCategory"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleAddCategory"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="addingCategory || !selectedCategoryToAdd"
          >
            {{ addingCategory ? t('dashboard.adding') : t('common.add') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Remove Category Modal -->
  <div
    v-if="showRemoveCategoryModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeRemoveCategoryModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('dashboard.removeCategoryFromSelected') }}</h2>

        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ t('dashboard.selectCategory') }}
          </label>
          <select
            v-model="selectedCategoryToRemove"
            class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-orange-500"
          >
            <option value="">{{ t('dashboard.chooseCategory') }}</option>
            <option
              v-for="category in categories"
              :key="category.uuid"
              :value="category.uuid"
            >
              {{ category.name }}
            </option>
          </select>
        </div>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeRemoveCategoryModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="removingCategory"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleRemoveCategory"
            class="px-4 py-2 bg-orange-600 text-sm font-medium text-white rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="removingCategory || !selectedCategoryToRemove"
          >
            {{ removingCategory ? t('dashboard.removing') : t('common.remove') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Send Message Modal -->
  <div
    v-if="showSendMessageModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeSendMessageModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('dashboard.sendMessageToSelected') }}</h2>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ t('dashboard.messageContent') }}
          </label>
          <textarea
            v-model="messageText"
            rows="5"
            maxlength="5000"
            class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-500 resize-none"
            :placeholder="t('dashboard.enterMessageContent')"
          ></textarea>
          <div class="text-xs text-gray-500 mt-1">
            {{ messageText.length }} / 5000
          </div>
        </div>

        <div class="mb-6">
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="sendEmailWithMessage"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.sendEmailToo') }}</span>
          </label>
        </div>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeSendMessageModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="sendingMessage"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleSendMessage"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="sendingMessage || !messageText.trim()"
          >
            {{ sendingMessage ? t('dashboard.sending') : t('common.send') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Payment Modal -->
  <div
    v-if="showAddPaymentModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeAddPaymentModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('dashboard.addPaymentToSelected') }}</h2>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ t('dashboard.paymentName') }} <span class="text-red-500">*</span>
          </label>
          <input
            v-model="paymentName"
            type="text"
            maxlength="255"
            class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-500"
            :placeholder="t('dashboard.enterPaymentName')"
          />
        </div>

        <div class="mb-4 grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('dashboard.paymentAmount') }} <span class="text-red-500">*</span>
            </label>
            <input
              v-model="paymentAmount"
              type="number"
              step="0.01"
              min="0.01"
              class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-500"
              :placeholder="t('dashboard.enterAmount')"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('dashboard.daysToPay') }} <span class="text-red-500">*</span>
            </label>
            <input
              v-model="paymentDaysToPay"
              type="number"
              min="0"
              class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-500"
              :placeholder="t('dashboard.enterDays')"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ t('dashboard.bankAccount') }} <span class="text-red-500">*</span>
          </label>
          <select
            v-model="paymentBankAccountUuid"
            class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-500"
          >
            <option value="">{{ t('dashboard.chooseBankAccount') }}</option>
            <option
              v-for="account in bankAccounts"
              :key="account.uuid"
              :value="account.uuid"
            >
              {{ account.account_name }} ({{ account.account_number }})
            </option>
          </select>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ t('dashboard.paymentDescription') }}
          </label>
          <textarea
            v-model="paymentDescription"
            rows="3"
            maxlength="1000"
            class="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-blue-500 resize-none"
            :placeholder="t('dashboard.enterDescription')"
          ></textarea>
        </div>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeAddPaymentModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="addingPayment"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleAddPayment"
            class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="addingPayment || !paymentName.trim() || !paymentAmount || !paymentDaysToPay || !paymentBankAccountUuid"
          >
            {{ addingPayment ? t('dashboard.adding') : t('dashboard.addPayment') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Export Modal -->
  <div
    v-if="showExportModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeExportModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('dashboard.exportPlayers') }}</h2>

        <p class="text-sm text-gray-600 mb-4">{{ t('dashboard.exportColumns') }}</p>

        <div class="space-y-2">
          <!-- First name - always checked, disabled -->
          <label class="flex items-center cursor-not-allowed">
            <input
              type="checkbox"
              :checked="true"
              disabled
              class="w-4 h-4 text-blue-600 border-gray-300 rounded opacity-60"
            />
            <span class="ml-2 text-sm text-gray-500">{{ t('dashboard.colFirstName') }} <span class="text-xs text-gray-400">({{ t('dashboard.requiredColumn') }})</span></span>
          </label>

          <!-- Last name - always checked, disabled -->
          <label class="flex items-center cursor-not-allowed">
            <input
              type="checkbox"
              :checked="true"
              disabled
              class="w-4 h-4 text-blue-600 border-gray-300 rounded opacity-60"
            />
            <span class="ml-2 text-sm text-gray-500">{{ t('dashboard.colLastName') }} <span class="text-xs text-gray-400">({{ t('dashboard.requiredColumn') }})</span></span>
          </label>

          <!-- License number -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.license_number"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colLicenseNumber') }}</span>
          </label>

          <!-- Gender -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.gender"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colGender') }}</span>
          </label>

          <!-- Categories -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.categories"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colCategories') }}</span>
          </label>

          <!-- Birth date -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.birth_date"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colBirthDate') }}</span>
          </label>

          <!-- Registration date -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.registered_at"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colRegisteredAt') }}</span>
          </label>

          <!-- Phone -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.phone"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colPhone') }}</span>
          </label>

          <!-- Email -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.email"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colEmail') }}</span>
          </label>

          <!-- Medical exam date -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.medical_exam_date"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colMedicalExamDate') }}</span>
          </label>

          <!-- Guardian 1 -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.guardian_1"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colGuardian1') }}</span>
          </label>

          <!-- Guardian 2 -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.guardian_2"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colGuardian2') }}</span>
          </label>

          <!-- Street -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.street"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colStreet') }}</span>
          </label>

          <!-- City -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.city"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colCity') }}</span>
          </label>

          <!-- Postal code -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.postal_code"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colPostalCode') }}</span>
          </label>

          <!-- School -->
          <label class="flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="exportColumns.school_name"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="ml-2 text-sm text-gray-700">{{ t('dashboard.colSchoolName') }}</span>
          </label>
        </div>

        <div class="flex gap-3 justify-end mt-6">
          <button
            @click="closeExportModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="exporting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleExport"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="exporting"
          >
            {{ exporting ? t('dashboard.exporting') : t('dashboard.export') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <PlayerImportModal v-if="showImportModal" @close="showImportModal = false" />
</template>

<style scoped>

</style>
